import { isActionOf } from 'typesafe-actions'
import { RootEpic } from '../../root'
import { distinctUntilChanged, filter, map, mergeMap, pairwise, switchMap } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import { concat, EMPTY, from, of } from 'rxjs'
import { groupBy } from 'fp-ts/es6/NonEmptyArray'
import { apiRequests } from '../../../http/config'
import { filesLens } from '../../../utils/lenses'
import { defaultValue, isDefined, pick, values } from '../../../utils/utils'
import { commentSelector, objectSelector, projectSelector, tasksCommentsSelector } from '../../../utils/selectors'
import { flow, pipe } from 'fp-ts/es6/function'
import { EMPTY_PROJECT } from '../../../responses/projects'
import * as A from 'fp-ts/es6/Array'
import * as R from 'fp-ts/es6/Record'
import * as O from 'fp-ts/es6/Option'
import { Comment } from '../../../responses/comments'
import { shallowEqual } from 'react-redux'
import { rowResponse } from '../../../utils/rx-operators'
import { projectActions } from '../../data/project/project-actions'
import { entityEqPredicate, eqEntityOpt } from '../../../utils/entity-utils'
import { galleryActions } from './gallery-actions'
import { RequestStatus } from '../../../responses/response'
import { taskActions } from '../../data/task/task-actions'
import { historyActions } from '../history/history-actions'
import { FileData } from '../../../responses/files'
import { contramap, ordString } from 'fp-ts/Ord'
import { EMPTY_PROJECT_WORK_OBJECT } from '../../../responses/objects'
import { objectActions } from '../../data/object/object-actions'
import { GalleryEditState } from './gallery-reducer'
import { HistoryType } from '../../../constants/enums'

const groupFiles = (owner: HistoryType) => flow(
    groupBy((file: FileData) => `${file.user.id} ${file.upload_group_id}`),
    R.mapWithIndex((id, files) => {
        const comment: Comment = {
            id,
            created_at: files[0].created_at,
            resulted: false,
            owner_id: 0,
            owner_type: owner,
            files,
            text: '',
            deleted: false,
            edited: false,
            user: files[0].user,
            user_id: files[0].user.id,
        }
        return comment
    }),
    values,
)

const requests: RootEpic = action$ => action$.pipe(
    filter(isActionOf(galleryActions.open)),
    mergeMap(action => {
        switch (action.payload.type) {
            case 'task':
            case 'comment':
                return concat(
                    of(taskActions.parents.request(action.payload)),
                    from(action.payload.tasksId.map(taskId => taskActions.comments.request({
                        taskId,
                        resulted: action.payload.type === 'task',
                    })))
                )
            default:
                return EMPTY
        }
    })
)

const getComments: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(galleryActions.open)),
    switchMap(action => {
        switch (action.payload.type) {
            case 'none':
                return EMPTY
            case 'single':
                return of([action.payload.comment])
            case 'task':
                return state$.pipe(
                    map(tasksCommentsSelector(action.payload.tasksId)),
                )
            case 'comment':
                return state$.pipe(
                    map(state => state.global.gallery.ids.map(id => commentSelector(id)(state)).filter(isDefined)),
                )
            case 'object':
                return state$.pipe(
                    map(flow(
                        objectSelector(action.payload.objectId),
                        defaultValue(EMPTY_PROJECT_WORK_OBJECT),
                        filesLens.get,
                    )),
                    distinctUntilChanged(shallowEqual),
                    map(groupFiles('project_work_object'))
                )
            case 'project':
                return state$.pipe(
                    map(flow(
                        projectSelector(action.payload.projectId),
                        defaultValue(EMPTY_PROJECT),
                        filesLens.get,
                    )),
                    distinctUntilChanged(shallowEqual),
                    map(groupFiles('project'))
                )
        }
    }),
    map((comments: Comment[]) => pipe(
        comments,
        A.filter(comment => A.isNonEmpty(comment.files)),
        A.sort(contramap((comment: Comment) => comment.created_at)(ordString)),
    )),
    distinctUntilChanged(shallowEqual),
    map(galleryActions.setComments),
)

const remove: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(galleryActions.delete.request)),
    mergeMap(() => GalleryEditState.match({
        delete: ({file}) => {
            switch (file.owner_type) {
                case 'project_work_object':
                    return apiRequests.workObject.update({
                        params: {objectId: file.owner_id},
                        data: { files: { delete: [file.id]}}
                    }).pipe(
                        rowResponse('Не удалось удалить файл из объекта'),
                        map(object => objectActions.update.success(object, 'system'))
                    )
                case 'project':
                    return apiRequests.project.update({
                        params: { projectId: file.owner_id },
                        data: { files: { delete: [file.id] } }
                    }).pipe(
                        rowResponse('Не удалось удалить файл из проекта'),
                        map(project => projectActions.update.success(project, 'system'))
                    )
                case 'comment':
                    return apiRequests.comment.update({
                        params: { id: file.owner_id },
                        data: { files: { delete: [file.id] } }
                    }).pipe(
                        rowResponse('Не удалось удалить файл из комментария'),
                        map(comment => historyActions.comment.success(comment, 'system'))
                    )
            }
        }
    }, () => EMPTY)(state$.value.global.gallery.editState)),
    mergeMap(action => from([action, galleryActions.delete.success()]))
)

const commentFiles = (comment: Comment) => comment.files

const checkCurrent: RootEpic = (_, state$) => state$.pipe(
    map(state => pick(state.global.gallery, 'comments', 'current')),
    distinctUntilChanged(shallowEqual),
    pairwise(),
    mergeMap(([oldGallery, gallery]) => {
        const { status, data } = state$.value.global.gallery

        const oldFiles = oldGallery.comments.flatMap(commentFiles)
        const files = gallery.comments.flatMap(commentFiles)
        if (!(data.type === 'single')
            && (['task', 'comment'].includes(data.type) ? status === RequestStatus.SUCCESS : true)
            && !A.elem(eqEntityOpt)(gallery.current, files)
        ) {
            return of(galleryActions.setCurrent(pipe(
                oldFiles,
                A.findIndex(entityEqPredicate(gallery.current)),
                O.chain(index => A.lookup(index)(files)),
                O.alt(() => A.head(files)),
                O.getOrElseW(() => null),
            )))
        } else {
            return EMPTY
        }
    }),
)

export const galleryEpic = combineEpics(getComments, remove, checkCurrent, requests)